// Widget stylesheet. It lives inside the widget's shadow root, so the host
// page's CSS can't reach in and ours can't leak out. Every branding value is
// fed in through CSS custom properties set on .xq-root by render.ts.

import { fontStack } from "./fonts";
import type { WidgetBranding } from "./types";

const SHADOWS = {
  none: "none",
  soft: "0 8px 28px rgba(15, 23, 42, 0.10)",
  strong: "0 18px 50px rgba(15, 23, 42, 0.22)",
};

/**
 * Readable text colour (dark navy or white) for a solid background hex.
 * Used for the "EPC" tag, which fills with the accent colour: a light accent
 * (our lime default) needs dark text, a dark accent (an installer's navy) white.
 * Falls back to dark text for anything that isn't a #rrggbb hex.
 */
function readableOn(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#202937";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255,
    g = (n >> 8) & 255,
    b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 150 ? "#FFFFFF" : "#202937";
}

/** CSS custom properties for a branding config (applied as an inline style). */
export function cssVars(b: WidgetBranding): Record<string, string> {
  return {
    "--xq-font": fontStack(b.font.family),
    "--xq-primary": b.colors.primary,
    "--xq-primary-text": b.colors.primaryText,
    "--xq-bg": b.colors.background,
    "--xq-text": b.colors.text,
    "--xq-muted": b.colors.mutedText,
    "--xq-border": b.colors.border,
    "--xq-input-bg": b.colors.inputBackground,
    "--xq-accent": b.colors.accent,
    // Text that sits ON the accent fill (the EPC tag), kept readable on any accent
    "--xq-accent-text": readableOn(b.colors.accent),
    "--xq-radius": `${b.shape.radius}px`,
    "--xq-btn-radius": `${b.shape.buttonRadius}px`,
    "--xq-shadow": SHADOWS[b.shape.shadow],
    "--xq-canvas-border": b.shape.border ? `1px solid ${b.colors.border}` : "none",
    "--xq-width": b.layout.fullWidth && b.layout.mode === "inline" ? "100%" : `${b.layout.width}px`,
    "--xq-height": b.layout.height ? `${b.layout.height}px` : "auto",
    "--xq-pad": `${b.layout.padding}px`,
    "--xq-offset-x": `${b.layout.offsetX}px`,
    "--xq-offset-y": `${b.layout.offsetY}px`,
  };
}

export const WIDGET_CSS = /* css */ `
:host { all: initial; display: block; }
* { box-sizing: border-box; }

.xq-root {
  font-family: var(--xq-font);
  color: var(--xq-text);
  font-size: 15px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}

/* ── Inline: sits in the page flow ─────────────────────────────── */
.xq-inline { display: flex; }
.xq-inline.xq-align-left { justify-content: flex-start; }
.xq-inline.xq-align-center { justify-content: center; }
.xq-inline.xq-align-right { justify-content: flex-end; }

/* ── The canvas (both modes) ───────────────────────────────────── */
.xq-canvas {
  width: var(--xq-width);
  max-width: 100%;
  height: var(--xq-height);
  display: flex;
  flex-direction: column;
  background: var(--xq-bg);
  border: var(--xq-canvas-border);
  border-radius: var(--xq-radius);
  box-shadow: var(--xq-shadow);
  overflow: hidden;
}
.xq-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--xq-pad);
}

/* ── Floating: launcher button + panel pinned to a corner ──────── */
.xq-floating {
  position: fixed;
  bottom: var(--xq-offset-y);
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: calc(100vw - 32px);
}
.xq-floating.xq-pos-bottom-right { right: var(--xq-offset-x); align-items: flex-end; }
.xq-floating.xq-pos-bottom-left { left: var(--xq-offset-x); align-items: flex-start; }
/* In the console preview the widget is pinned to the preview frame, not the window */
.xq-contained .xq-floating { position: absolute; max-width: calc(100% - 32px); pointer-events: auto; }

/* Console preview only (highlight option): mark where the floating widget sits.
   Never set on installers' websites. */
.xq-highlight .xq-floating { outline: 2px dashed #202937; outline-offset: 8px; border-radius: 16px; }
.xq-highlight .xq-floating::before {
  content: "Your widget";
  position: absolute; top: -21px; padding: 2px 10px; border-radius: 999px;
  background: #202937; color: #fff; font: 600 11px/1.5 system-ui, sans-serif; white-space: nowrap;
}
.xq-highlight .xq-pos-bottom-right::before { right: 0; }
.xq-highlight .xq-pos-bottom-left::before { left: 0; }
.xq-floating .xq-canvas { max-height: min(var(--xq-height-max, 640px), calc(100vh - 120px)); }
.xq-contained .xq-floating .xq-canvas { max-height: calc(100% - 80px); }
.xq-launcher {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 999px;
  background: var(--xq-primary);
  color: var(--xq-primary-text);
  font: inherit;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
}
.xq-launcher svg { width: 18px; height: 18px; }

/* ── Header: logo, close (floating), progress ─────────────────── */
.xq-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
.xq-logo { max-height: 36px; max-width: 160px; display: block; }
.xq-close {
  margin-left: auto; width: 32px; height: 32px; border: none; border-radius: 50%;
  background: transparent; color: var(--xq-muted); cursor: pointer; font-size: 20px; line-height: 1;
}
.xq-close:hover { color: var(--xq-text); }
.xq-progress { height: 4px; border-radius: 2px; background: var(--xq-border); overflow: hidden; margin-bottom: 22px; }
.xq-progress > span { display: block; height: 100%; background: var(--xq-primary); transition: width 200ms ease; }

/* ── Typography ────────────────────────────────────────────────── */
.xq-title { margin: 0 0 6px; font-size: 22px; line-height: 1.25; font-weight: 700; color: var(--xq-text); }
.xq-sub { margin: 0 0 20px; color: var(--xq-muted); }
.xq-label { display: block; margin: 0 0 6px; font-size: 13px; font-weight: 700; }
.xq-hint { margin: 10px 0 0; font-size: 13px; color: var(--xq-muted); }
.xq-error { margin: 12px 0 0; font-size: 13px; color: #B42318; }

/* ── Controls ──────────────────────────────────────────────────── */
.xq-field { margin-bottom: 16px; }
.xq-input, .xq-select {
  width: 100%; height: 46px; padding: 0 14px;
  border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius);
  background: var(--xq-input-bg); color: var(--xq-text); font: inherit;
}
.xq-input:focus, .xq-select:focus { outline: 2px solid var(--xq-primary); outline-offset: 1px; }
.xq-row { display: flex; gap: 10px; }
.xq-row .xq-input { flex: 1; min-width: 0; }
.xq-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  height: 46px; padding: 0 20px; border: none; border-radius: var(--xq-btn-radius);
  background: var(--xq-primary); color: var(--xq-primary-text);
  font: inherit; font-weight: 700; cursor: pointer; white-space: nowrap;
}
.xq-btn:disabled { opacity: 0.55; cursor: default; }
.xq-btn-block { width: 100%; }
/* Secondary choice (e.g. "Request a callback" beside "Get my detailed quote") */
.xq-btn-ghost { background: transparent; color: var(--xq-primary); box-shadow: inset 0 0 0 2px var(--xq-primary); margin-top: 10px; }
.xq-done a.xq-btn { text-decoration: none; margin: 8px 0 12px; }
.xq-link { background: none; border: none; padding: 0; font: inherit; font-size: 13px; color: var(--xq-muted); cursor: pointer; text-decoration: underline; }

/* Option chips (property type, heating) */
.xq-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.xq-chip {
  padding: 9px 14px; border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius);
  background: var(--xq-input-bg); color: var(--xq-text); font: inherit; font-size: 14px; cursor: pointer;
}
.xq-chip[aria-pressed="true"] { border-color: var(--xq-primary); background: var(--xq-primary); color: var(--xq-primary-text); }

/* EPC pre-fill: the certificate's answer carries a small "EPC" tag and an
   accent ring, so it stays identifiable even if the homeowner picks another */
.xq-chip { display: inline-flex; align-items: center; gap: 6px; }
.xq-chip-epc { box-shadow: 0 0 0 2px var(--xq-accent); border-color: transparent; }
.xq-chip-epc[aria-pressed="true"] { box-shadow: 0 0 0 2px var(--xq-bg), 0 0 0 4px var(--xq-accent); }
.xq-epc-tag {
  padding: 1px 5px; border-radius: 4px; font-size: 10px; font-weight: 700; letter-spacing: 0.4px;
  background: var(--xq-accent); color: var(--xq-accent-text);
}
.xq-from-epc {
  margin-left: 6px; padding: 1px 6px; border-radius: 4px; font-size: 11px; font-weight: 600;
  /* Fixed light grey, not brand-tinted: an informational "from the certificate"
     marker that must stay legible on any accent colour */
  background: #9CA3AF; color: #FFFFFF;
}
/* The pre-selected EPC answer doesn't need its own "EPC" tag — being selected
   is obvious. The tag only appears on the EPC chip once the homeowner picks a
   different answer, so they can still see what the certificate said. */
.xq-chip[aria-pressed="true"] .xq-epc-tag { display: none; }
.xq-epc-note {
  margin: 0 0 18px; padding: 12px 14px; border-radius: var(--xq-btn-radius); font-size: 13px; line-height: 1.45;
  background: color-mix(in srgb, var(--xq-accent) 18%, var(--xq-bg)); border-left: 3px solid var(--xq-accent);
}
.xq-epc-none { background: color-mix(in srgb, var(--xq-border) 35%, var(--xq-bg)); border-left-color: var(--xq-border); }

/* Address list */
.xq-list { display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow-y: auto; margin-bottom: 16px; }
.xq-option {
  text-align: left; padding: 12px 14px; border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius);
  background: var(--xq-input-bg); color: var(--xq-text); font: inherit; font-size: 14px; cursor: pointer;
}
.xq-option[aria-pressed="true"] { border-color: var(--xq-primary); box-shadow: inset 0 0 0 1px var(--xq-primary); }

/* Quote result */
.xq-quote {
  margin: 4px 0 18px; padding: 20px; border-radius: var(--xq-radius);
  background: color-mix(in srgb, var(--xq-accent) 22%, var(--xq-bg));
  border-left: 4px solid var(--xq-accent);
}
.xq-quote-range { font-size: 30px; line-height: 1.15; font-weight: 700; letter-spacing: -0.5px; }
.xq-quote-meta { margin-top: 6px; font-size: 13px; color: var(--xq-muted); }
.xq-facts { display: grid; grid-template-columns: auto 1fr; gap: 6px 16px; margin: 0 0 18px; font-size: 14px; }
.xq-facts dt { color: var(--xq-muted); }
.xq-facts dd { margin: 0; }

/* "How is this estimate worked out?": the published-method statement */
.xq-method { margin-top: 10px; font-size: 13px; color: var(--xq-muted); }
.xq-method summary { cursor: pointer; text-decoration: underline; }
.xq-method p { margin: 8px 0 0; line-height: 1.5; }
/* Step-by-step calculation: label + arithmetic on the left, result on the right */
.xq-working table { width: 100%; margin-top: 8px; border-collapse: collapse; font-size: 13px; }
.xq-working th, .xq-working td { padding: 7px 0; border-top: 1px solid var(--xq-border); vertical-align: top; text-align: left; }
.xq-working th { font-weight: 600; color: var(--xq-text); }
.xq-working th span { display: block; font-weight: 400; color: var(--xq-muted); }
.xq-working td { padding-left: 12px; text-align: right; white-space: nowrap; font-weight: 600; color: var(--xq-text); font-variant-numeric: tabular-nums; }
.xq-working tr:last-child td { color: var(--xq-primary); }

.xq-consent { display: flex; gap: 10px; align-items: flex-start; font-size: 13px; color: var(--xq-muted); margin-bottom: 16px; }
.xq-consent input { margin-top: 2px; width: 16px; height: 16px; accent-color: var(--xq-primary); }

.xq-done { text-align: center; padding: 12px 0; }
.xq-done-icon {
  width: 56px; height: 56px; margin: 0 auto 16px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--xq-primary); color: var(--xq-primary-text);
}

/* ── Footer: Powered by xenscope (always shown) ────────────────── */
.xq-foot {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px var(--xq-pad); border-top: 1px solid var(--xq-border);
  font-size: 11px; color: var(--xq-muted);
}
.xq-foot a { display: inline-flex; align-items: center; gap: 4px; color: inherit; text-decoration: none; font-weight: 700; }
.xq-foot svg { width: 7px; height: 12px; }

/* Narrow canvas (phones, or a narrow widget): a container query, so it reacts
   to the widget's own width. That keeps the console's mobile preview identical
   to a real phone, where a @media query would see the console's window instead. */
.xq-canvas { container-type: inline-size; }
@container (max-width: 420px) {
  .xq-row { flex-direction: column; }
  /* Stacked: full-height input (flex: 1 would collapse it vertically) and a full-width button */
  .xq-row .xq-input { flex: none; }
  .xq-row .xq-btn { width: 100%; }
  .xq-title { font-size: 20px; }
  .xq-quote-range { font-size: 26px; }
}
`;
